import Defer from "../defer";
import Protocol from "./protocol";
import EventService from "../event.service";
import { SocketService, ConnectStatus } from "../websocket/socket.service";
import TransferAPI from "./api/transfer";
import { EventMessage, ServerInfo } from "./model-type";
import { getDomain } from "../../utils/domain";
import {
  CHECK,
  LOGIN,
  ACTIVE_WINDOW,
  UPDATE_ALL_NORMAL_TASK,
} from "./command-type";
import {
  HAS_LOGGED,
  LOGGED_OUT,
  UNDEFINED,
  CHECK_SUCCESS,
  CONNECT_FAILED,
  RECEIVE_TASK_COUNT,
  PING_PEDDING,
  PING_SUCCESS,
  PING_FAILED,
  DISCONNECT,
  UPLOAD,
} from "./event-type";
import { STORAGE } from "../../constants";

const HOSTNAME = "local.raysync.cn"; // NOTE: this domain resolves to 127.0.0.1
const PROTOCOL = "wss:";
const PORT_LIST = [9527, 19527, 29527, 39527, 49527, 59527];

export enum Route {
  Side,
  Main,
}
const initialConfig = {
  host: "",
  port: "",
  proxyHost: "",
  proxyPort: "",
  clientVersion: "",
  protocolVersion: "",
  server5Port: 0,
  server5SSLPort: 0,
  renderbusProxyPort: 0,
  tcpEnable: 0,
  tcpDelay: 0,
};

async function getServerInfo(
  platform?: string,
  shareKey?: string
): Promise<ServerInfo[]> {
  const option = platform ? { headers: { platform } } : undefined;
  const params: { zone: number; shareKey?: string } = {
    zone: getDomain() ? 2 : 1,
  };
  if (platform) {
    params.shareKey = shareKey;
  }
  const response = await TransferAPI.getTransferServerMsg(params, option);
  try {
    if (!response.data) {
      throw new Error("shareKey is invalid");
    }
    if (!response || (!response.data && !response.data.raysyncTransfer)) {
      return [initialConfig, initialConfig];
    }
    const serverIp = response.data.raysyncTransfer.serverIp;
    const ipv4Reg = /^([0-9]{1,3})\.([0-9]{1,3})\.([0-9]{1,3})\.([0-9]{1,3})$/;
    const domainReg =
      /^(?=.{1,253}$)(?!-)(?:[a-zA-Z0-9-]{1,63}\.)+[a-zA-Z0-9-]{2,63}$/;
    const ipv4 = ipv4Reg.test(serverIp) || domainReg.test(serverIp);
    const tcpEnable = ipv4 ? 0 : 2;
    const tcpDelay = 10;

    const normalConfig = {
      host: response.data.raysyncTransfer.serverIp,
      port: response.data.raysyncTransfer.serverPort,
      proxyHost: response.data.raysyncTransfer.proxyIp,
      proxyPort: response.data.raysyncTransfer.proxyPort,
      clientVersion: response.data.clientVersion,
      protocolVersion: response.data.protocolVersion,
      server5Port: response.data.raysyncTransfer.port,
      server5SSLPort: response.data.raysyncTransfer.sslPort,
      renderbusProxyPort: response.data.raysyncTransfer.renderbusProxyPort || 0,
      tcpEnable,
      tcpDelay,
    };

    // if version < 2.6.8.10
    if (
      !response.data.raysyncTransfer.input ||
      !response.data.raysyncTransfer.output
    ) {
      return [normalConfig, normalConfig];
    }

    // if version >= 2.6.8.10
    const inputConfig = {
      host: response.data.raysyncTransfer.serverIp,
      port: response.data.raysyncTransfer.input.serverPort,
      proxyHost: response.data.raysyncTransfer.proxyIp,
      proxyPort: response.data.raysyncTransfer.proxyPort,
      clientVersion: response.data.clientVersion,
      protocolVersion: response.data.protocolVersion,
      server5Port: response.data.raysyncTransfer.input.port,
      server5SSLPort: response.data.raysyncTransfer.input.sslPort,
      renderbusProxyPort: response.data.raysyncTransfer.renderbusProxyPort || 0,
      tcpEnable,
      tcpDelay,
      webServerIp: response.data.raysyncTransfer.webServerIp,
      webServerPort: response.data.raysyncTransfer.webServerPort,
    };
    const outputConfig = {
      host: response.data.raysyncTransfer.serverIp,
      port: response.data.raysyncTransfer.output.serverPort,
      proxyHost: response.data.raysyncTransfer.proxyIp,
      proxyPort: response.data.raysyncTransfer.proxyPort,
      clientVersion: response.data.clientVersion,
      protocolVersion: response.data.protocolVersion,
      server5Port: response.data.raysyncTransfer.output.port,
      server5SSLPort: response.data.raysyncTransfer.output.sslPort,
      renderbusProxyPort: response.data.raysyncTransfer.renderbusProxyPort || 0,
      tcpEnable,
      tcpDelay,
    };
    return [inputConfig, outputConfig];
  } catch (e) {
    console.trace(e);
    return [initialConfig, initialConfig];
  }
}

/**
 * Base use
 * import { RECEIVE_PREFERENCE } from '@/services/event-type'
 * import { PREFERENCE } from '@/services/command-type'
 * import RaySyncIns from '@/services/raysync'
 *
 * RaySyncIns.subscribe(RECEIVE_PREFERENCE, handler)
 * RaySyncIns.unsubscribe(RECEIVE_PREFERENCE, handler)
 *
 * Use executeCommand API to send message to RaySync client
 * RaySyncIns.executeCommand(PREFERENCE, data)
 */
class RaySync extends SocketService {
  hasLogin: boolean;
  // NOTE: false when check command response mismatch version or sign
  checkFail: boolean;
  checkSuccess: boolean;
  clickSync: boolean;
  handleError: (code: number | undefined) => void = () => {};
  portCount: number;
  serverInfo: ServerInfo[];
  // NOTE: download connect is captain, upload is wingman
  // NOTE: if version >= 2.8.6.10, use input/output config to identify connect
  wingman: WebSocket;
  isWingmanLogin: boolean;
  config: any;
  wingmanConnectStatus: ConnectStatus;
  constructor(private protocol: Protocol, config: any) {
    super(new EventService());
    this.config = config;
    this.hasLogin = false;
    this.checkFail = false;
    this.checkSuccess = false;
    this.clickSync = false;
    this.portCount = PORT_LIST.length;
    this.isWingmanLogin = false;
    this.wingmanConnectStatus = ConnectStatus.CLOSED;
    this.eventBus.register(HAS_LOGGED, () => {
      this.hasLogin = true;
      document.addEventListener("visibilitychange", (e) => {
        if (!document.hidden) {
          this.connection.send(this.protocol[ACTIVE_WINDOW](true));
        }
      });
    });
    this.eventBus.register(LOGGED_OUT, () => {
      this.hasLogin = false;
    });
    this.eventBus.register(CHECK_SUCCESS, () => {
      this.connection.send(this.protocol[LOGIN](this.serverInfo[1]));
    });
    let intervalID: any;
    this.eventBus.register(RECEIVE_TASK_COUNT, ({ count }) => {
      if (count !== 0 && !intervalID) {
        intervalID = setInterval(() => {
          TransferAPI.sendHeartBeat();
        }, 20 * 60 * 1000);
      } else if (intervalID && count === 0) {
        clearInterval(intervalID);
        intervalID = null;
      }
    });
    // TODO: error detect
  }
  initUserInfo(account: string, token: string, line: string, platform: string) {
    this.protocol.initUserInfo(account, token, line, platform);
  }
  updateUserInfo({ key, value }: { key: string; value: string }) {
    this.protocol.updateUserInfo(key, value);
  }
  executeCommand(command: string, route = Route.Side, ...params) {
    if (
      (this.connectStatus === ConnectStatus.CONNECTED && this.hasLogin) ||
      (this.wingmanConnectStatus === ConnectStatus.CONNECTED &&
        this.isWingmanLogin)
    ) {
      this.proxySend(this.protocol[command](...params), route);
    } else {
      this.eventBus.register(
        HAS_LOGGED,
        () => this.proxySend(this.protocol[command](...params), route),
        true,
        command
      );
      if (this.connectStatus === ConnectStatus.CONNECTED && !this.hasLogin) {
        return;
      }
      this.eventBus.post(PING_PEDDING);
      this.connectStatus === ConnectStatus.CONNECTING
        ? this.eventBus.register(
            PING_FAILED,
            () => this.ping(false),
            true,
            command
          )
        : this.ping(false);
    }
  }
  // NOTE: for multi command register
  doSomethingIfLogin(
    handler: (...args) => void,
    key: string,
    route = Route.Side
  ) {
    if (
      (this.connectStatus === ConnectStatus.CONNECTED &&
        this.hasLogin &&
        route === Route.Main) ||
      (this.wingmanConnectStatus === ConnectStatus.CONNECTED &&
        this.isWingmanLogin &&
        route === Route.Side)
    ) {
      handler();
    } else {
      this.eventBus.register(HAS_LOGGED, handler, true, key);
      // NOTE: if version check fails, that means this connect can't send login command
      if (
        this.connectStatus === ConnectStatus.CONNECTED &&
        !this.hasLogin &&
        !this.checkFail &&
        route === Route.Main
      ) {
        // NOTE: can login again this moment
        this.connection.send(this.protocol[LOGIN](this.serverInfo[1]));
        return;
      }
      if (
        this.wingmanConnectStatus === ConnectStatus.CONNECTED &&
        !this.isWingmanLogin &&
        route === Route.Side &&
        !this.config.isPicturePlatform()
      ) {
        this.wingman.send(this.protocol[LOGIN](this.serverInfo[0]));
        return;
      }
      this.eventBus.post(PING_PEDDING);
      this.connectStatus === ConnectStatus.CONNECTING
        ? this.eventBus.register(PING_FAILED, () => this.ping(false), true, key)
        : this.ping(false);
    }
  }
  async ping(silent: boolean = true, rePing: boolean = false) {
    this.connectStatus = ConnectStatus.CONNECTING;
    if (rePing || !this.serverInfo) {
      const platform = this.config.getPlatform && this.config.getPlatform();
      const shareKey = this.config.shareKey;
      this.serverInfo = await getServerInfo(platform, shareKey);
      if (sessionStorage.getItem(STORAGE.SWITCH_RAYSYNC)) {
        sessionStorage.setItem(STORAGE.SWITCH_RAYSYNC, "");
        try {
          const outputServerInfo = this.serverInfo[1];
          const inputServerInfo = this.serverInfo[0];
          const executeRoute = location.host.includes("task.renderbus.com")
            ? Route.Main
            : Route.Side;
          this.doSomethingIfLogin(
            () => {
              this.executeCommand(UPDATE_ALL_NORMAL_TASK, Route.Main, {
                task_type: "download",
                server5_port: +(outputServerInfo.server5Port || 0),
                server_ip: outputServerInfo.host,
                server_port: +(outputServerInfo.port || 0),
                request_id: "ountput_" + Date.now(),
                proxy_ip: outputServerInfo.proxyHost,
                proxy_port: +(outputServerInfo.proxyPort || 0),
                renderbus_proxy_port: +(
                  outputServerInfo.renderbusProxyPort || 0
                ),
                server5_ssl_port: +(outputServerInfo.server5SSLPort || 0),
                renderbus_id: sessionStorage.getItem("platform") || "",
              });
              this.executeCommand(UPDATE_ALL_NORMAL_TASK, Route.Main, {
                task_type: "upload",
                server5_port: +(inputServerInfo.server5Port || 0),
                server_ip: inputServerInfo.host,
                server_port: +(inputServerInfo.port || 0),
                request_id: "input_" + Date.now(),
                proxy_ip: inputServerInfo.proxyHost,
                proxy_port: +(inputServerInfo.proxyPort || 0),
                renderbus_proxy_port: +(
                  inputServerInfo.renderbusProxyPort || 0
                ),
                server5_ssl_port: +(inputServerInfo.server5SSLPort || 0),
                renderbus_id: sessionStorage.getItem("platform") || "",
              });
            },
            "update_all_task",
            executeRoute
          );
        } catch (error) {
          console.trace(error);
        }
      }
    }
    const createSocketIns = (host: string, port: number): Promise<boolean> => {
      const defer = new Defer<boolean>();
      const ins = new WebSocket(`${PROTOCOL}//${host}:${port}`);
      ins.onopen = (event: Event) => {
        this.checkConnectClient(ins).then((isConnected) => {
          if (isConnected) {
            this.connection = ins;
            this.initConnection();
            if (!this.config.isPicturePlatform()) {
              this.initWingmanConnection(host, port);
            }
            defer.resolve(true);
          } else {
            ins.close();
            defer.resolve(false);
          }
        });
      };
      ins.onerror = (event: ErrorEvent) => defer.resolve(false);
      return defer.promise;
    };
    const promises = PORT_LIST.map((port) => createSocketIns(HOSTNAME, port));
    const results = await Promise.all(promises);
    if (results.filter((r) => r).length !== 0) {
      this.connectStatus = ConnectStatus.CONNECTED;
      this.eventBus.post(PING_SUCCESS);
    } else {
      this.eventBus.post(PING_FAILED);
      this.connectStatus = ConnectStatus.FAILED;
      !silent && this.eventBus.post(CONNECT_FAILED);
    }
  }
  protected defaultHandleMessageCallback({
    eventName,
    data,
    success,
    code,
  }: EventMessage) {
    if (!success) {
      this.handleError(code);
      this.eventBus.post(eventName);
      return;
    }

    if (eventName === DISCONNECT) {
      this.connection.close();
      return;
    }

    if (eventName === CHECK_SUCCESS) {
      this.checkFail = !success;
      this.checkSuccess = true;
    }
    // TODO: log collection
    this.eventBus.post(eventName, data);
  }
  protected async handleMessage(event: MessageEvent, callback) {
    const eventMessage = await this.protocol.parseMessage(event.data);
    if (callback) {
      callback(eventMessage);
    } else {
      this.defaultHandleMessageCallback(eventMessage);
    }
  }
  protected handleClose(event: CloseEvent) {
    this.connectStatus = ConnectStatus.CLOSED;
    if (this.hasLogin) {
      this.hasLogin = false;
      this.reconnect();
    }
  }
  private checkConnectClient(ins) {
    const defer = new Defer<boolean>();
    const messageHandler = async (event: MessageEvent) => {
      await this.handleMessage(event, (eventMessage) => {
        if (eventMessage.eventName === CHECK_SUCCESS) {
          defer.resolve(true);
        } else {
          defer.resolve(false);
        }
      });
    };
    ins.onmessage = messageHandler;
    ins.send(
      this.protocol[CHECK](
        this.serverInfo[1].clientVersion,
        this.serverInfo[1].protocolVersion
      )
    );
    return defer.promise;
  }
  private initConnection() {
    this.connection.onmessage = this.handleMessage.bind(this);
    this.connection.onclose = this.handleClose.bind(this);
    this.connection.send(
      this.protocol[CHECK](
        this.serverInfo[1].clientVersion,
        this.serverInfo[1].protocolVersion
      )
    );
  }
  private reconnect() {
    const intervalID = setInterval(() => {
      if (this.connectStatus === ConnectStatus.CONNECTED) {
        clearInterval(intervalID);
      } else if (this.connectStatus === ConnectStatus.CLOSED) {
        this.ping();
      }
    }, 5000);
  }
  private initWingmanConnection(host: any, port: any) {
    this.wingman = new WebSocket(`${PROTOCOL}//${host}:${port}`);
    this.wingman.onopen = () => {
      this.wingmanConnectStatus = ConnectStatus.CONNECTED;
      if (!this.config.isPicturePlatform()) {
        this.wingman.send(this.protocol[LOGIN](this.serverInfo[0]));
      }
    };
    this.wingman.onmessage = async (event) => {
      const { eventName, success, data, code } =
        await this.protocol.parseMessage(event.data);
      if (!success) {
        this.handleError(code);
        return;
      }

      if (eventName === DISCONNECT) {
        this.wingman.close();
        return;
      }

      if (eventName === HAS_LOGGED) {
        this.isWingmanLogin = success;
      }
      if (eventName === RECEIVE_TASK_COUNT && success) {
        this.eventBus.post(eventName, data);
      }

      if (eventName === UPLOAD) {
        this.eventBus.post(eventName, data);
      }
    };
    this.wingman.onclose = () => {
      this.wingmanConnectStatus = ConnectStatus.CLOSED;
    };
  }
  private proxySend(data: string, route: Route) {
    switch (route) {
      case Route.Main:
        this.connection.send(data);
        break;
      case Route.Side:
        this.wingman.send(data);
        break;
    }
  }
}

// sorry, i implement this service couple with business, please refactor it
export default RaySync;
